import { formatDateDisplay } from '../../utils/date';
import { isBrandKey } from '../../config/brands';
import { BRAND_PROFILES } from '../../config/brandProfiles';

/**
 * Renders a settlement lag in the coarsest unit that still reads precisely.
 * ACH wallet top-ups routinely take days, so "4d 4h" beats "361620s".
 */
export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const m = minutes % 60;
    return m > 0 ? `${hours}h ${m}m` : `${hours}h`;
  }
  const days = Math.floor(hours / 24);
  const h = hours % 24;
  return h > 0 ? `${days}d ${h}h` : `${days}d`;
}

/**
 * An entity a caller resolved from a row's `entity_id`. Several emitters write the id and
 * nothing else — `payment_reminder_sent` and `invoice_voided` carry no metadata at all — so
 * the names have to be looked up before a summary can say who the row is about.
 */
export type ResolvedEntity =
  | {
      kind: 'invoice';
      studentName: string | null;
      programName: string | null;
      totalAmount: number | null;
      balanceDue: number | null;
      status: string | null;
      dueDate: string | null;
    }
  | { kind: 'enrollment'; studentName: string | null; programName: string | null }
  | { kind: 'payment'; studentName: string | null; programName: string | null; method: string | null }
  | { kind: 'program'; name: string | null }
  | { kind: 'student'; name: string | null };

/**
 * Everything a summary may read beyond `metadata`: the row's own columns, plus the lookups
 * the caller performed on its behalf. All optional — a summary degrades to whatever the
 * emitter happened to write when nothing was resolved.
 */
export interface EventSummaryContext {
  page_url?: string | null;
  /** Resolved from the row's `entity_id`, interpreted per its `entity_type`. */
  entity?: ResolvedEntity | null;
  /** Resolved from `metadata.student_id` — the attendance emitters write the id, not a name. */
  student?: string | null;
  /** Resolved from `metadata.program_id`. */
  program?: string | null;
  /** `line_item_configs.id` → name, for the opt-out ids in an enrollment edit diff. */
  lineItems?: Record<string, string>;
}

/**
 * Returns a concise, human-readable summary of an app_events row's metadata,
 * keyed by event_type. Pure function — extracted from AdminAnalytics so it can be
 * unit-tested. Used for the Details column, mobile cards, and the session modal.
 *
 * `row` carries the columns a summary may need. The auth events read the page URL, which
 * used to be copied into `metadata.url` on every event; that duplication is gone, so new
 * rows are read from the `page_url` column and older ones still fall back to the metadata
 * key. `row.entity` and the id lookups beside it are how the admin-side emitters — which
 * log an `entity_id` and no metadata — get a name in the Details column at all.
 */
export function formatEventSummary(
  eventType: string,
  metadata: Record<string, unknown>,
  row: EventSummaryContext = {},
): string {
    const m = metadata ?? {};
    const pageUrl = m.url ?? row.page_url ?? null;
    const entity = row.entity ?? null;
    const invoice = entity?.kind === 'invoice' ? entity : null;
    const enrollment = entity?.kind === 'enrollment' ? entity : null;
    const payment = entity?.kind === 'payment' ? entity : null;
    // Nothing to summarise, and no column or lookup to fall back on.
    if (Object.keys(m).length === 0 && !pageUrl && !entity && !row.student && !row.program) return '';
    const str = (v: unknown) => (v != null ? String(v) : '');
    const money = (v: unknown) => (v != null ? `$${Number(v).toFixed(2)}` : '');
    const shortId = (v: unknown) => {
      const value = str(v);
      return value ? `${value.substring(0, 8)}…` : '';
    };
    const pathFromUrl = (v: unknown) => {
      const value = str(v);
      if (!value) return '';

      try {
        return new URL(value).pathname;
      } catch {
        return value;
      }
    };
    const methodLabel = (v: unknown) => {
      const value = str(v);
      return value ? value.toUpperCase() : '';
    };
    // Only a key the app still knows is named. An event written under a brand since removed
    // keeps its raw key rather than being relabelled as the default brand, which is what
    // `brandProfileOf`'s fallback would quietly do to it.
    const brandLabel = (v: unknown) => {
      const value = str(v);
      return isBrandKey(value) ? BRAND_PROFILES[value].shortName : value;
    };
    const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;
    // A name the emitter wrote always wins; the lookups only fill in what it left out, so a
    // resolver that returns nothing can never make an existing summary worse.
    const studentName =
      str(m.student_name) ||
      row.student ||
      invoice?.studentName ||
      enrollment?.studentName ||
      payment?.studentName ||
      (entity?.kind === 'student' ? entity.name : '') ||
      '';
    const programName =
      str(m.program_name) ||
      row.program ||
      invoice?.programName ||
      enrollment?.programName ||
      payment?.programName ||
      (entity?.kind === 'program' ? entity.name : '') ||
      '';

    switch (eventType) {
      case 'auth_login':
      case 'auth_signup':
        // `metadata.browser` holds the raw user-agent; the Device column already renders it
        // legibly, so it stays out of here.
        return [
          str(m.provider) ? `Provider: ${str(m.provider)}` : '',
          pathFromUrl(pageUrl),
        ].filter(Boolean).join(' · ');
      case 'auth_logout':
        return pathFromUrl(pageUrl);
      // The account page's own sign-out button, which records where it was pressed.
      case 'user_signout':
        return [
          str(m.location) ? `from ${str(m.location)}` : '',
          pathFromUrl(pageUrl),
        ].filter(Boolean).join(' · ');
      case 'program_view':
        return programName;
      case 'program_enroll_start':
        return programName;
      case 'program_enroll_complete':
        return [
          [programName, studentName].filter(Boolean).join(' — '),
          // Set when an admin enrolled on a parent's behalf, which reads very differently
          // from a parent enrolling themselves.
          m.on_behalf_of_parent_id ? 'by admin' : '',
          Number(m.enrollment_count) > 1 ? plural(Number(m.enrollment_count), 'enrollment') : '',
        ].filter(Boolean).join(' · ');
      case 'program_enroll_abandon':
        return programName;
      case 'program_unenroll':
      case 'program_unenroll_start':
      case 'program_unenroll_complete':
      case 'program_unenroll_failed':
        return [programName, studentName].filter(Boolean).join(' — ');
      case 'enrollment_edit':
      case 'enrollment_edit_complete':
      case 'enrollment_edit_failed':
        return [programName, studentName].filter(Boolean).join(' — ');
      case 'student_create': {
        // The completed row carries the child's details; the modal-opened row carries only
        // the action, so both shapes have to render.
        const created = [str(m.student_first_name), str(m.student_last_name)].filter(Boolean).join(' ');
        return [
          created || studentName,
          str(m.student_grade) ? `grade ${str(m.student_grade)}` : '',
          str(m.student_school),
          str(m.action),
          str(m.source),
        ].filter(Boolean).join(' · ');
      }
      case 'student_update':
      case 'student_delete':
        return studentName;
      case 'payment_start':
        return [
          studentName,
          programName,
          money(m.amount),
          str(m.payment_method) ? `via ${str(m.payment_method)}` : '',
          // Where the one-invoice checkout was opened from (bundle-payment adoption).
          m.source === 'pay_only_this' ? 'from cart: Pay only this' : str(m.source) ? `from ${str(m.source).replace(/_/g, ' ')}` : '',
        ].filter(Boolean).join(' · ');
      case 'payment_complete':
        return [
          studentName,
          programName,
          money(m.amount),
          str(m.payment_method) ? `via ${str(m.payment_method)}` : '',
          // What Stripe kept. Recorded on every card/ACH payment but never shown, so the
          // net take was only visible by opening Stripe.
          Number(m.stripe_fee) > 0 ? `fee ${money(m.stripe_fee)}` : '',
        ].filter(Boolean).join(' · ');
      case 'payment_failed':
        return [
          studentName,
          money(m.amount),
          str(m.error_message) || str(m.error),
        ].filter(Boolean).join(' · ');
      case 'payment_abandon':
        return [studentName, programName, money(m.amount)].filter(Boolean).join(' · ');
      // Bundle payment (cart). Amounts on these events are in cents.
      case 'cart_banner_view':
      case 'cart_checkout_click':
        return [
          m.invoice_count != null ? `${str(m.invoice_count)} due` : '',
          m.due_cents != null ? money(Number(m.due_cents) / 100) : '',
        ].filter(Boolean).join(' · ');
      case 'cart_checkout_start':
        return [
          m.invoice_count != null ? `${str(m.invoice_count)} invoices` : '',
          m.total_cents != null ? money(Number(m.total_cents) / 100) : '',
          str(m.method) ? `via ${str(m.method)}` : '',
        ].filter(Boolean).join(' · ');
      case 'cart_payment_success':
        return [
          m.invoice_count != null ? `${str(m.invoice_count)} invoices` : '',
          m.amount_cents != null ? money(Number(m.amount_cents) / 100) : '',
          str(m.method) ? `via ${str(m.method)}` : '',
          Number(m.residue_cents) > 0 ? `${money(Number(m.residue_cents) / 100)} to BHCS Cash` : '',
        ].filter(Boolean).join(' · ');
      case 'cart_payment_failed':
        return [
          m.invoice_count != null ? `${str(m.invoice_count)} invoices` : '',
          m.total_cents != null ? money(Number(m.total_cents) / 100) : '',
          str(m.method) ? `via ${str(m.method)}` : '',
        ].filter(Boolean).join(' · ');
      case 'add_payment_method':
        return [
          methodLabel(m.payment_method) || methodLabel(m.method) || methodLabel(m.payment_method_type),
          str(m.action),
          str(m.status),
          str(m.location),
        ].filter(Boolean).join(' · ');
      case 'update_payment_method':
        return [
          methodLabel(m.payment_method) || methodLabel(m.method) || methodLabel(m.payment_method_type),
          str(m.action),
          str(m.status),
        ].filter(Boolean).join(' · ');
      case 'invoice_view':
        return [
          studentName,
          programName,
          m.total_amount != null ? `Total ${money(m.total_amount)}` : '',
          Number(m.amount_paid) > 0 ? `Paid ${money(m.amount_paid)}` : '',
          m.balance_due != null ? `Due ${money(m.balance_due)}` : '',
          str(m.invoice_status),
        ].filter(Boolean).join(' · ');
      case 'invoice_download':
        return [
          [studentName, programName].filter(Boolean).join(' — '),
          str(m.download_type),
          str(m.invoice_status),
        ].filter(Boolean).join(' · ');
      // Void, comp and the invoice email all log an `entity_id` and (usually) nothing else,
      // so everything below the amount comes from the resolved invoice.
      case 'invoice_voided':
        return [
          studentName,
          programName,
          m.amount != null ? money(m.amount) : (invoice?.totalAmount != null ? money(invoice.totalAmount) : ''),
        ].filter(Boolean).join(' · ');
      case 'invoice_comped': {
        const comped = m.comped_amount != null ? m.comped_amount : invoice?.totalAmount;
        return [
          studentName,
          programName,
          comped != null ? `comped ${money(comped)}` : '',
          str(m.previous_status) ? `was ${str(m.previous_status)}` : '',
          str(m.note),
        ].filter(Boolean).join(' · ');
      }
      case 'invoice_email_sent':
        return [
          studentName,
          programName,
          str(m.recipient) || str(m.email),
          invoice?.status ? `now ${invoice.status}` : '',
        ].filter(Boolean).join(' · ');
      // Which way the parent moved, and whether they asked to. `trigger` is spelled out rather
      // than shown raw: "passive" alone does not say that the portal, not the parent, moved them.
      case 'brand_switch': {
        const from = brandLabel(str(m.from_brand));
        const to = brandLabel(str(m.to_brand));
        return [
          from && to ? `${from} → ${to}` : to,
          str(m.trigger) === 'passive' ? `followed a link (${str(m.source)})` : str(m.source),
        ].filter(Boolean).join(' · ');
      }
      case 'coupon_apply':
        return [str(m.coupon_code), str(m.discount)].filter(Boolean).join(' · ');
      case 'coupon_remove':
        return str(m.coupon_code) || '';
      case 'stripe_webhook':
        return [
          str(m.status),
          str(m.payment_intent_id) ? `PI: ${str(m.payment_intent_id).substring(0, 16)}…` : '',
          m.invoice_id ? `Invoice: ${shortId(m.invoice_id)}` : '',
          m.payment_id ? `Payment: ${shortId(m.payment_id)}` : '',
        ].filter(Boolean).join(' · ');
      case 'recurring_invoice':
        return [
          str(m.status),
          m.period_start && m.period_end ? `${str(m.period_start)} → ${str(m.period_end)}` : '',
        ].filter(Boolean).join(' · ');
      case 'autopay_payment':
        return [
          money(m.amount),
          str(m.status),
          str(m.payment_method_type) ? `via ${str(m.payment_method_type)}` : '',
          m.invoice_id ? `Invoice: ${shortId(m.invoice_id)}` : '',
        ].filter(Boolean).join(' · ');
      // The FAQ has sub-pages; which one was opened is the only thing worth saying.
      case 'view_faq':
        return pathFromUrl(pageUrl) || str(m.page);
      case 'autopay_toggle':
        return str(m.enabled) === 'true' ? 'Enabled' : str(m.enabled) === 'false' ? 'Disabled' : str(m.action) || '';
      case 'set_default_payment_method':
        return methodLabel(m.payment_method) || methodLabel(m.method_type) || methodLabel(m.method) || '';
      case 'waiver_view':
        return [
          studentName,
          m.eligible_student_count != null
            ? `${plural(Number(m.eligible_student_count), 'student')} eligible`
            : '',
        ].filter(Boolean).join(' · ');
      case 'waiver_submit': {
        const ids = Array.isArray(m.student_ids) ? m.student_ids.length : null;
        const consents =
          m.consents && typeof m.consents === 'object'
            ? Object.values(m.consents as Record<string, unknown>)
            : null;
        return [
          studentName,
          ids != null ? plural(ids, 'student') : '',
          // A partial signature is the interesting case — photo release declined, say.
          consents ? `${consents.filter(Boolean).length}/${consents.length} consents` : '',
        ].filter(Boolean).join(' · ');
      }
      case 'attendance_absence_set':
        return [
          studentName,
          programName,
          str(m.session_date),
          str(m.reason) ? `reason: ${str(m.reason)}` : 'no reason given',
          str(m.previous_status) === 'checked_in' ? 'corrected a check-in' : '',
          str(m.actor_name) ? `by ${str(m.actor_name)}` : '',
        ].filter(Boolean).join(' · ');
      case 'attendance_absence_cleared':
        return [
          studentName,
          programName,
          str(m.session_date),
          str(m.cleared_via) === 'check_in' ? 'arrived late' : 'undone',
          str(m.previous_reason) ? `was: ${str(m.previous_reason)}` : '',
          str(m.actor_name) ? `by ${str(m.actor_name)}` : '',
        ].filter(Boolean).join(' · ');
      // `attendance_pickup_recorded` is the retired name: it logged every check-out for a
      // day before being narrowed to exceptions only. Those rows carry the same shape and
      // include authorized releases, so the badge is still driven off the field.
      case 'attendance_pickup_recorded':
      case 'attendance_pickup_unauthorized':
        return [
          studentName,
          str(m.picked_up_by) ? `→ ${str(m.picked_up_by)}` : '',
          m.is_authorized === false ? '⚠ NOT on authorized list' : 'authorized',
          str(m.session_date),
          str(m.actor_name) ? `released by ${str(m.actor_name)}` : '',
        ].filter(Boolean).join(' · ');
      case 'flex_plan_save': {
        const month = str(m.plan_month).slice(0, 7);
        const added = Array.isArray(m.days_added) ? m.days_added.length : null;
        const removed = Array.isArray(m.days_removed) ? m.days_removed.length : null;
        // A net-zero edit still moved days around, so show the swap rather than "no change".
        const delta = added != null && removed != null && (added > 0 || removed > 0)
          ? `${added > 0 ? `+${added}` : ''}${added > 0 && removed > 0 ? ' / ' : ''}${removed > 0 ? `−${removed}` : ''}`
          : '';
        const previous = Number(m.previous_day_count);
        const days = Number.isFinite(previous) && previous !== Number(m.day_count)
          ? `${previous} → ${str(m.day_count)} days`
          : `${str(m.day_count)} days`;
        return [
          month,
          days,
          delta,
          str(m.status),
          m.allow_locked === true ? 'past lock' : '',
        ].filter(Boolean).join(' · ');
      }
      case 'payment_preferences_update':
        return [
          str(m.scope),
          m.autopay_enabled === true ? 'autopay on' : m.autopay_enabled === false ? 'autopay off' : '',
          m.fallback_changed === true
            ? (m.fallback_payment_method_set === true ? 'fallback set' : 'fallback cleared')
            : '',
        ].filter(Boolean).join(' · ');
      case 'signup_registration_create':
      case 'signup_registration_cancel':
      case 'signup_registration_move':
        return [
          studentName,
          str(m.event_name),
          str(m.meeting_type),
          eventType === 'signup_registration_move' ? 'moved slot' : '',
        ].filter(Boolean).join(' · ');
      case 'admin_enrollment_unenroll':
        return [studentName, programName].filter(Boolean).join(' — ');
      case 'parent_profile_update': {
        const fields = Array.isArray(m.changed_fields) ? m.changed_fields.join(', ') : '';
        return [
          str(m.parent_name) || str(m.parent_email),
          fields ? `changed: ${fields}` : 'no field changes',
          m.edited_by_admin === true ? 'by admin' : '',
        ].filter(Boolean).join(' · ');
      }
      case 'admin_coupon_grant':
        return [
          str(m.code),
          str(m.coupon_type),
          m.value != null ? String(m.value) : '',
          m.granted_to_user_id ? `→ user ${shortId(m.granted_to_user_id)}` : '',
        ].filter(Boolean).join(' · ');
      case 'admin_printable_coupons_create':
        return [
          m.count != null ? `${str(m.count)} coupons` : '',
          str(m.theme_name),
          money(m.amount),
        ].filter(Boolean).join(' · ');
      case 'admin_eligible_rule_create':
      case 'admin_eligible_rule_update':
      case 'admin_eligible_rule_delete':
        return [
          str(m.scope),
          str(m.value),
          m.discount_value != null
            ? `${str(m.discount_value)}${str(m.discount_type) === 'percentage' ? '%' : ''}`
            : '',
        ].filter(Boolean).join(' · ');
      case 'admin_teacher_create':
      case 'admin_teacher_update':
      case 'admin_teacher_delete':
        return [
          str(m.teacher_name) || str(m.teacher_email),
          m.scoped_to_all_programs === true
            ? 'all programs'
            : m.program_count != null ? `${str(m.program_count)} programs` : '',
        ].filter(Boolean).join(' · ');
      case 'admin_email_template_update':
        return [
          str(m.template_key),
          Array.isArray(m.changed_fields) && m.changed_fields.length > 0
            ? `changed: ${m.changed_fields.join(', ')}`
            : '',
        ].filter(Boolean).join(' · ');
      case 'admin_email_test_send':
        return [
          str(m.template_key),
          Array.isArray(m.recipients) ? `→ ${m.recipients.join(', ')}` : '',
        ].filter(Boolean).join(' · ');
      case 'admin_line_item_config_create':
      case 'admin_line_item_config_update':
      case 'admin_line_item_config_delete':
        return [str(m.name), str(m.type), str(m.subtype)].filter(Boolean).join(' · ');
      case 'admin_student_photo_import':
        return [
          m.uploaded != null ? `${str(m.uploaded)} uploaded` : '',
          Number(m.failed) > 0 ? `${str(m.failed)} failed` : '',
        ].filter(Boolean).join(' · ');
      case 'student_parent_link':
      case 'student_parent_unlink':
        return [
          str(m.student_name) || str(m.parent_email),
          m.count != null ? `${str(m.count)} link${Number(m.count) === 1 ? '' : 's'}` : '',
        ].filter(Boolean).join(' · ');
      case 'authorized_pickup_add':
      case 'authorized_pickup_remove':
        return [
          str(m.pickup_name),
          str(m.relationship) ? `(${str(m.relationship)})` : '',
          str(m.phone_last4) ? `···${str(m.phone_last4)}` : '',
          str(m.student_name) ? `for ${str(m.student_name)}` : '',
        ].filter(Boolean).join(' · ');
      // Sent one invoice at a time from the debt tab, with the invoice id and no metadata.
      case 'payment_reminder_sent':
        return [
          studentName,
          programName,
          invoice?.dueDate ? `due ${formatDateDisplay(invoice.dueDate)}` : '',
          // Current state, not the state at send time — hence the label. Worth seeing:
          // whether the reminder ever landed.
          invoice ? `now ${invoice.status ?? 'unknown'}` : money(m.amount),
          invoice?.balanceDue != null && invoice.balanceDue > 0
            ? `${money(invoice.balanceDue)} outstanding`
            : '',
        ].filter(Boolean).join(' · ');
      case 'late_fee_notice_sent': {
        const rate = typeof m.late_fee_rate === 'number' ? `${Math.round(m.late_fee_rate * 1000) / 10}%` : '';
        return [studentName, programName, rate, money(m.late_fee_amount)].filter(Boolean).join(' · ');
      }
      case 'feature_flag_override': {
        const flag = str(m.flag) || 'unknown flag';
        const value = str(m.override_value);
        const canonical = m.canonical_value === true ? 'ON' : 'OFF';
        const valueLabel = value === 'on' ? 'Force ON' : value === 'off' ? 'Force OFF' : 'Reset to default';
        return `${flag} → ${valueLabel} (settings.json: ${canonical})`;
      }
      case 'agent_query': {
        const parts = [str(m.query)];
        if (m.from_cache === true) parts.push('cached');
        if (m.is_sql_based === true) parts.push('SQL');
        return parts.filter(Boolean).join(' · ');
      }
      case 'parent_agent_query': {
        const preview = (v: unknown, n: number) => {
          const s = str(v);
          return s.length > n ? `${s.slice(0, n)}…` : s;
        };
        let source = 'static';
        if (m.is_sql_based === true) {
          const n = Number(m.query_count);
          source = Number.isFinite(n) && n > 0 ? `SQL ×${n}` : 'SQL';
        }
        return [
          str(m.query) ? `Q: "${str(m.query)}"` : '',
          str(m.answer) ? `A: ${preview(m.answer, 200)}` : '',
          source,
          m.needs_clarification === true ? 'clarify' : '',
        ].filter(Boolean).join(' · ');
      }
      case 'parent_agent_view': {
        // The assistant is opened with the question prefilled from a deep link; that `q`
        // param is the only record of what the parent came in wanting to ask.
        let question = '';
        try {
          if (pageUrl) question = new URL(String(pageUrl)).searchParams.get('q') ?? '';
        } catch {
          // A `page_url` that is not a URL simply has no query string to read.
        }
        return [
          str(m.source) ? `from ${str(m.source)}` : '',
          question ? `Q: "${question}"` : '',
        ].filter(Boolean).join(' · ');
      }
      case 'parent_agent_feedback': {
        const rating = Number(m.rating);
        const max = Number(m.max_rating) || 3;
        const stars = Number.isFinite(rating) && rating > 0
          ? '★'.repeat(rating) + '☆'.repeat(Math.max(0, max - rating))
          : '';
        return [
          stars ? `${stars} (${rating}/${max})` : '',
          str(m.query) ? `Q: "${str(m.query)}"` : '',
        ].filter(Boolean).join(' · ');
      }
      case 'error_occurred':
        return str(m.error_message) || str(m.error) || str(m.message) || '';
      case 'admin_program_create':
      case 'admin_program_update':
      case 'admin_program_delete':
        // `action` distinguishes an enrolment lock from an ordinary field edit.
        return [programName, str(m.action)].filter(Boolean).join(' · ');
      case 'admin_coupon_create':
        return str(m.coupon_code) || str(m.code) || '';
      case 'cash_purchase_start':
      case 'cash_purchase_pending':
      case 'cash_purchase_complete':
      case 'cash_purchase_failed': {
        const methodNames: Record<string, string> = { card: 'Card', ach: 'ACH', zelle: 'Zelle' };
        const method = methodNames[str(m.method)] ?? str(m.method);
        const cents = (v: unknown) => (v != null ? `$${(Number(v) / 100).toFixed(2)}` : '');
        const parts = [
          cents(m.amount_cents),
          method ? `via ${method}` : '',
          str(m.parent_email),
        ];
        if (Number(m.bonus_cents) > 0) parts.push(`+${cents(m.bonus_cents)} bonus`);
        if (eventType === 'cash_purchase_complete' && m.settlement_seconds != null) {
          parts.push(`settled in ${formatDuration(Number(m.settlement_seconds))}`);
        }
        if (eventType === 'cash_purchase_complete' && m.new_balance_cents != null) {
          parts.push(`balance ${cents(m.new_balance_cents)}`);
        }
        if (eventType === 'cash_purchase_failed') parts.push(str(m.error_message));
        if (m.memo_code) parts.push(`memo ${str(m.memo_code)}`);
        return parts.filter(Boolean).join(' · ');
      }
      case 'admin_cash_grant':
        return [
          money(m.amount_dollars),
          str(m.bucket) ? `(${str(m.bucket)})` : '',
          str(m.parent_email) ? `→ ${str(m.parent_email)}` : '',
          str(m.note),
        ].filter(Boolean).join(' · ');
      case 'admin_refund_process':
        return [
          studentName,
          programName,
          money(m.amount),
          str(m.refund_method),
        ].filter(Boolean).join(' · ');
      case 'admin_enrollment_status_toggle':
        return [str(m.action), programName, studentName].filter(Boolean).join(' · ');
      case 'admin_enrollment_edit': {
        const changes = (m.changes ?? {}) as Record<string, unknown>;
        const nested = (changes.metadata ?? {}) as Record<string, unknown>;
        // Events before 3.69.0 recorded the declined ids; newer ones record the chosen ids.
        // app_events is immutable history, so both shapes still have to render.
        const optIns = Array.isArray(nested.opt_in_items) ? nested.opt_in_items : null;
        const optOuts = optIns ?? (Array.isArray(nested.opt_out_items) ? nested.opt_out_items : null);
        const selectedDays = Array.isArray(nested.selected_days) ? nested.selected_days : null;
        const detail: string[] = [];
        if (optOuts) {
          // The diff holds `line_item_configs` ids; only the resolved names mean anything
          // to whoever is reading the trail.
          const names = optOuts.map(id => row.lineItems?.[String(id)] ?? '').filter(Boolean);
          const verb = optIns ? 'add-ons' : 'opted out of';
          if (optOuts.length === 0) detail.push(optIns ? 'no add-ons' : 'no opt-outs');
          else if (names.length === optOuts.length) detail.push(`${verb} ${names.join(', ')}`);
          else detail.push(`${verb} ${plural(optOuts.length, 'item')}`);
        }
        if (selectedDays) {
          detail.push(selectedDays.length > 0 ? `${plural(selectedDays.length, 'day')} selected` : 'no days selected');
        }
        const otherKeys = Object.keys(changes).filter(k => k !== 'metadata');
        if (otherKeys.length > 0) detail.push(`changed: ${otherKeys.join(', ')}`);
        return [[programName, studentName].filter(Boolean).join(' — '), ...detail]
          .filter(Boolean).join(' · ');
      }
      case 'admin_bulk_deactivate_enrollments': {
        const parts: string[] = [];
        if (m.program_count != null) parts.push(`${String(m.program_count)} programs`);
        if (m.enrollment_count != null) parts.push(`${String(m.enrollment_count)} enrollments`);
        if (parts.length === 0 && m.program_names) parts.push(str(m.program_names));
        return parts.join(' · ');
      }
      case 'admin_invoice_edit': {
        const transition = m.status_changed === true && str(m.old_status) && str(m.new_status)
          ? `${str(m.old_status)} → ${str(m.new_status)}`
          : '';
        const edited = Number(m.edited_line_items_count) || 0;
        const removed = Number(m.removed_line_items_count) || 0;
        const touched = [
          edited > 0 ? `${plural(edited, 'item')} edited` : '',
          removed > 0 ? `${plural(removed, 'item')} removed` : '',
        ].filter(Boolean).join(', ');
        return [
          studentName,
          programName,
          money(m.amount),
          transition,
          touched,
          // The save round-tripped without altering the status or any line item.
          !transition && !touched && m.line_items_changed === false ? 'no net change' : '',
        ].filter(Boolean).join(' · ');
      }
      case 'admin_late_fee_added':
      case 'admin_late_fee_waived': {
        const fee = m.late_fee_amount != null ? m.late_fee_amount : m.amount;
        const before = Number(m.previous_total);
        const delta = Number(m.late_fee_amount);
        const swing = Number.isFinite(before) && Number.isFinite(delta)
          ? `total ${money(before)} → ${money(eventType === 'admin_late_fee_waived' ? before - delta : before + delta)}`
          : '';
        return [
          studentName,
          programName,
          fee != null ? money(fee) : '',
          str(m.late_fee_description),
          swing,
        ].filter(Boolean).join(' · ');
      }
      case 'admin_signup_event_create':
      case 'admin_signup_event_edit':
      case 'admin_signup_event_duplicate':
      case 'admin_signup_event_delete':
        return str(m.event_name) || str(m.title) || '';
      case 'admin_signup_registration_delete':
        return str(m.student_name) || '';
      case 'zelle_email': {
        const parts: string[] = [];
        if (m.status) parts.push(str(m.status));
        if (m.student_name) parts.push(str(m.student_name));
        if (m.program_name) parts.push(str(m.program_name));
        if (m.amount != null) parts.push(money(m.amount));
        if (m.transaction_number) parts.push(`Txn: ${shortId(m.transaction_number)}`);
        if (m.sender) parts.push(`from ${str(m.sender)}`);
        // Why the matcher landed where it did — the whole reason to audit a Zelle row.
        if (m.confidence) parts.push(`${str(m.confidence)} confidence`);
        if (m.match_reason) parts.push(str(m.match_reason));
        return parts.join(' · ');
      }
      case 'co_parent_invite_sent':
        return [
          str(m.inviter_name),
          `→ ${str(m.invitee_email)}`,
          studentName ? `for ${studentName}` : '',
        ].filter(Boolean).join(' · ');
      case 'co_parent_invite_needs_onboarding':
        return [
          str(m.invitee_email),
          studentName ? `for ${studentName}` : '',
          str(m.inviter_name) ? `invited by ${str(m.inviter_name)}` : '',
          'needs onboarding',
        ].filter(Boolean).join(' · ');
      case 'co_parent_access_granted':
        return [
          str(m.inviter_name),
          `→ ${str(m.invitee_name) || str(m.invitee_email)}`,
          studentName ? `for ${studentName}` : '',
          'immediate access',
        ].filter(Boolean).join(' · ');
      case 'co_parent_invite_accepted': {
        const newParent = m.is_new_parent === true ? 'new account' : 'existing account';
        return [
          str(m.invitee_email),
          studentName ? `for ${studentName}` : '',
          str(m.inviter_name) ? `invited by ${str(m.inviter_name)}` : '',
          newParent,
        ].filter(Boolean).join(' · ');
      }
      default: {
        // Fallback: show key fields that are commonly useful
        const useful = [
          'program_name',
          'student_name',
          'event_name',
          'amount',
          'status',
          'action',
          'payment_method',
          'payment_method_type',
          'method',
          'invoice_status',
          'reason',
          'source',
          'location',
          'note',
        ];
        const parts = useful.map(k => (m[k] != null ? `${k === 'amount' ? money(m[k]) : str(m[k])}` : '')).filter(Boolean);
        return parts.join(' · ');
      }
    }
}
